export interface AvsState {
  error: string;
  isFetchingConfigurations: boolean;
  isFetchingVerificationRecords: boolean;
  isFetchingVerificationResult: boolean;
  isFetchingVerificationResultData: boolean;
  isSubmitting: boolean;
  isVerifying: boolean;
  configurations: AvsConfiguration[];
  updatedConfigurations: AvsUpdateConfigPayload[];
  verificationState: VerificationResponse<IbanVerificationResponsePayload> | null;
  verificationResultDetail: IbanVerificationDetails | null;
  verificationRecords: PaginatedResponse<IbanVerificationRecord> | null;
  bulkVerificationResponse: VerificationResponse<never> | null;
  bulkVerificationResult: IbanVerificationBulkDetails | null;
  bulkVerificationResultData: PaginatedResponse<IbanVerificationDetails> | null;
  // International verification state
  internationalVerificationRecords: PaginatedResponse<InternationalVerificationRecord> | null;
  internationalBulkVerificationResponse: VerificationResponse<never> | null;
  internationalBulkVerificationResult: InternationalVerificationBulkDetails | null;
  internationalBulkVerificationResultData: PaginatedResponse<InternationalVerificationDetails> | null;
  internationalVerificationResultDetail: InternationalVerificationDetails | null;
  isFetchingInternationalVerificationRecords: boolean;
  isFetchingInternationalVerificationResult: boolean;
  isFetchingInternationalVerificationResultData: boolean;
}

export interface AvsError {}

export enum AvsConfigStatus {
  enabled = 'ENABLED',
  disabled = 'DISABLED',
}

export type MatchAvailability = {
  match: AvsConfigStatus;
  no_match: AvsConfigStatus;
};

export enum AvsAccountType {
  personal = 'personal',
  freelancer = 'freelancer',
  business = 'business',
}

export type AvailabilityAccountType = {
  personal: MatchAvailability;
  freelancer: MatchAvailability;
  business: MatchAvailability;
};

export type AvsConfiguration = {
  country_code: string;
  bank_code: string;
  bank_name: {
    en: string;
    ar: string;
  };
  availability: {
    global: AvsConfigStatus;
    account_type: AvailabilityAccountType;
  };
  application_enablement: AvsConfigStatus;

  // data table props to meet table row constrain and track row selectable state
  id: string;
  isSelectable: boolean;
};

export type AvsUpdateConfigPayload = {
  country_code: string;
  bank_code: string;
  enablement: AvsConfigStatus;
};

export interface BankName {
  en: string;
  ar: string;
}

export interface IbanVerificationResponsePayload {
  iban_ownership_verified: boolean;
  swift_code: string;
  bank_name: BankName;
  bank_code: string;
  verification_method: string;
  account_status: string;
  account_holder_name: string;
  matching: Matching;
}

export enum AvsIcons {
  disrupted = 'AlertCircle',
  unavailable = 'CloseCircle',
  available = 'CheckmarkCircle',
}

export enum AvsStatusTooltip {
  disrupted = 'Disrupted',
  unavailable = 'Unavailable',
  available = 'Available',
}

export type AvsIconData = {
  tooltipText: string;
  iconName: AvsIcons;
  iconColor: string;
};

export enum AvsServiceStatus {
  disabledByLean = 'All verification responses disabled by Lean',
  disabledByAdmin = 'All verification responses disabled by Admin',
  enabled = 'All verification responses enabled',
  disrupted = 'Some services disrupted',
  unavailable = 'All services disrupted',

  // custom statuses for pending states
  disabledByAdminSyncPending = 'Disabled by Admin - Sync pending',
  enabledByAdminSyncPending = 'Enabled by Admin - Sync pending',
}

export enum AvsAccountTypes {
  PERSONAL = 'PERSONAL',
  BUSINESS = 'BUSINESS',
  FREELANCER = 'FREELANCER',
}

export enum AvsIdentificationType {
  FULL_NAME = 'FULL_NAME',
  NATIONAL_ID = 'NATIONAL_ID',
  UNIFIED_NUMBER = 'UNIFIED_NUMBER',
  COMMERCIAL_REGISTRATION = 'COMMERCIAL_REGISTRATION',
  FREELANCER_NUMBER = 'FREELANCER_NUMBER',
  EMIRATES_ID = 'EMIRATES_ID',
  TRADE_LICENCE = 'TRADE_LICENCE',
}

export interface AvsVerificationData {
  type: AvsAccountTypes | '';
  identifications: Identification[];
  iban: string;
}

export enum VerificationType {
  single = 'SINGLE',
  bulk = 'BULK',
}

export enum VerificationStatus {
  ok = 'OK',
  pending = 'PENDING',
  invalidParameters = 'INVALID_PARAMETERS',
  internalServerError = 'INTERNAL_SERVER_ERROR',
  failed = 'FAILED',
}

export interface IbanVerificationRecord {
  id: string;
  name: string;
  accounts_processed: number;
  status: VerificationStatus;
  verification_type: VerificationType;
  created_at: string;
}

export interface VerificationResponse<T> {
  status: VerificationStatus;
  results_id: string;
  message: string;
  timestamp: string;
  status_detail: {
    granular_status_code: string;
    status_additional_info: string;
  };
  verifications?: T;
}

export type IbanVerificationBulkDetails = {
  result: {
    verified: number;
    not_verified: number;
    total: number;
  };
  completed_at?: string;
  created_at: string;
  created_by: string;
};

export interface Identification {
  type: AvsIdentificationType;
  value: string;
}

export interface Matching {
  type: string;
  score: number;
}

export interface IbanVerificationDetails {
  id: string;
  granular_status_code: string;
  status_additional_details: string;
  iban_ownership_verified?: boolean;
  iban: string;
  account_holder_name: string;
  type: AvsAccountTypes;
  identifications: Identification[];
  swift_code: string;
  bank_name: BankName;
  bank_code: string;
  created_at: string;
  account_status?: string;
  matching?: Matching;
}

export interface PaginatedResponse<T> {
  content: T[];
  page: {
    size: number;
    number: number;
    total_pages: number;
    total_elements: number;
  };
}

export enum UpgradeType {
  AVS = 'AVS',
  AVS_PLUS = 'AVS_PLUS',
  INTERNATIONAL_AVS = 'INTERNATIONAL_AVS',
}

export interface AvsUpgradeRequest {
  upgrade_type: UpgradeType;
}

export const BANK_LOGO_URL_MAPPING: Record<string, string | undefined> = {
  'emirates nbd bank pjsc':
    'https://cdn.leantech.me/img/bank-assets/uae/glyphs/enbdx/g-color-enbdx_uae.png',
  'abu dhabi commercial bank':
    'https://cdn.leantech.me/img/bank-assets/uae/glyphs/adcb/g-color-adcb_uae.png',
  'd360 bank':
    'https://cdn.leantech.me/img/bank-assets/sau/glyphs/d360/g-color-d360_sau.png',
  'saudi national bank':
    'https://cdn.leantech.me/img/bank-assets/sau/glyphs/snb/g-color-snb_sau.png',
  'gulf international bank':
    'https://cdn.leantech.me/img/bank-assets/sau/glyphs/gib/g-color-gib_sau.png',
  'stc bank':
    'https://cdn.leantech.me/img/bank-assets/sau/glyphs/stc/g-color-stc_sau.png',
  'alrajhi bank':
    'https://cdn.leantech.me/img/bank-assets/sau/glyphs/rajhi/g-color-rajhi_sau.png',
  'saudi investment bank':
    'https://cdn.leantech.me/img/bank-assets/sau/glyphs/saib/g-color-saib_sau.png',
  'bank aljazira':
    'https://cdn.leantech.me/img/bank-assets/sau/glyphs/alj/g-color-alj_sau.png',
  'saudi awwal bank':
    'https://cdn.leantech.me/img/bank-assets/sau/glyphs/sabb/g-color-sabb_sau.png',
  'samba financial services':
    'https://cdn.leantech.me/img/bank-assets/sau/glyphs/samba/g-color-samba_sau.png',
  'first abu dhabi bank':
    'https://cdn.leantech.me/img/bank-assets/uae/glyphs/fab/g-color-fab_uae.png',
  'albilad bank':
    'https://cdn.leantech.me/img/bank-assets/sau/glyphs/bilad/g-color-bilad_sau.png',
  'alinma bank':
    'https://cdn.leantech.me/img/bank-assets/sau/glyphs/alinma/g-color-alinma_sau.png',
  'banque saudi fransi':
    'https://cdn.leantech.me/img/bank-assets/sau/glyphs/bsf/g-color-bsf_sau.png',
  'arab national bank':
    'https://cdn.leantech.me/img/bank-assets/sau/glyphs/anb/g-color-anb_sau.png',
  'riyad bank':
    'https://cdn.leantech.me/img/bank-assets/sau/glyphs/riyad/g-color-riyad_sau.png',
};

export interface InternationalCountry {
  country_code: string;
  country_name: string;
  enabled: boolean;
}

// International Verification Types
export interface InternationalVerificationRecord {
  id: string;
  name: string;
  accounts_processed: number;
  status: VerificationStatus;
  verification_type: VerificationType;
  created_at: string;
}

export interface InternationalVerificationDetails {
  id: string;
  granular_status_code: string;
  status_additional_details: string;
  account_ownership_verified?: boolean;
  account_id: string;
  account_holder_name: string;
  type: AvsAccountTypes;
  identifications: Identification[];
  swift_code: string;
  bank_name: BankName;
  bank_code: string;
  created_at: string;
  completed_at: string;
  account_status?: string;
  matching?: Matching;
  country_code?: string;
}

export type InternationalVerificationBulkDetails = {
  result: {
    verified: number;
    not_verified: number;
    total: number;
  };
  completed_at?: string;
  created_at: string;
  created_by: string;
};
